import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Menu as MenuIcon, X } from 'lucide-react';
import type { PublicMenuItem } from '@/features/themes/lib/useSiteMenu';
import type { MenuBlock } from '@/types/blocks';
import { PublicMenuNav, MenuLink } from './PublicMenuNav';
import { getDropdownHoverStyle, getMenuBackgroundStyle } from './PublicMenuNav';

interface ResponsiveMenuNavProps {
  menu: PublicMenuItem[];
  siteSlug: string;
  align?: MenuBlock['align'];
  dropdownRadius?: MenuBlock['dropdownRadius'];
  dropdownBackgroundColor?: string;
  dropdownHoverBackgroundColor?: string;
  dropdownTextColor?: string;
  hamburgerColor?: string;
  backgroundColor?: string;
  backgroundOpacity?: number;
  textStyle?: CSSProperties;
  /** false no preview do editor: o botão aparece, mas não abre o overlay sobre a UI do CMS. */
  interactive?: boolean;
}

/**
 * Menu do bloco "Menu" com `mobile: 'hamburger'` — navegação horizontal a
 * partir de `md`, botão + painel abaixo disso.
 *
 * Componente separado do `PublicMenuNav` (que é puro e compartilhado com o
 * header embutido do site, o qual já traz o próprio hambúrguer): aqui mora o
 * estado de aberto/fechado, e o nav de desktop é o mesmo `PublicMenuNav`.
 */
const PANEL_RADIUS_CLASS: Record<NonNullable<MenuBlock['dropdownRadius']>, string> = {
  none: 'rounded-none',
  sm: 'rounded-lg',
  md: 'rounded-xl',
  lg: 'rounded-2xl',
};

export function ResponsiveMenuNav({ menu, siteSlug, align = 'right', dropdownRadius = 'none', dropdownBackgroundColor, dropdownHoverBackgroundColor, dropdownTextColor, hamburgerColor, backgroundColor, backgroundOpacity = 100, textStyle, interactive = true }: ResponsiveMenuNavProps) {
  const [open, setOpen] = useState(false);
  const [top, setTop] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  // Ancora o painel logo abaixo do botão medindo o próprio botão — não depende
  // de nenhum ancestral `relative`, que as divs do header não têm.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      setTop(rect ? Math.max(8, rect.bottom + 8) : 8);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const desktop = window.matchMedia('(min-width: 768px)');
    const onBreakpoint = () => {
      if (desktop.matches) setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    desktop.addEventListener('change', onBreakpoint);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      desktop.removeEventListener('change', onBreakpoint);
    };
  }, [open]);

  if (menu.length === 0) return null;

  const itemLink = (item: PublicMenuItem, child = false) => (
    <MenuLink
      item={item}
      siteSlug={siteSlug}
      style={{ ...textStyle, color: dropdownTextColor || textStyle?.color, ...getDropdownHoverStyle(dropdownHoverBackgroundColor, dropdownBackgroundColor) }}
      onNavigate={() => setOpen(false)}
      className={
        child
          ? 'block rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-[var(--menu-submenu-hover-background)]'
          : 'block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-[var(--menu-submenu-hover-background)]'
      }
    />
  );

  return (
    <>
      <div className="hidden md:block">
        <PublicMenuNav menu={menu} siteSlug={siteSlug} style="horizontal" align={align} dropdownRadius={dropdownRadius} dropdownBackgroundColor={dropdownBackgroundColor} dropdownHoverBackgroundColor={dropdownHoverBackgroundColor} dropdownTextColor={dropdownTextColor} backgroundColor={backgroundColor} backgroundOpacity={backgroundOpacity} textStyle={textStyle} />
      </div>

      <button
        ref={buttonRef}
        type="button"
        aria-label={open ? 'Fechar menu' : 'Abrir menu'}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          if (interactive) setOpen((value) => !value);
        }}
        className="inline-flex items-center justify-center rounded-lg border border-slate-200 p-2 text-slate-700 md:hidden"
        // Um tamanho de fonte herdado não deve redimensionar o ícone.
        style={{
          color: hamburgerColor || textStyle?.color,
          ...getMenuBackgroundStyle(backgroundColor, backgroundOpacity),
        }}
      >
        {open ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
      </button>

      {interactive &&
        open &&
        createPortal(
          <div className="fixed inset-0 z-[60] md:hidden">
            <div className="absolute inset-0 bg-slate-900/30" onClick={() => setOpen(false)} aria-hidden />
            <nav
              id={panelId}
              style={{ top, ...getMenuBackgroundStyle(dropdownBackgroundColor || backgroundColor, backgroundOpacity) }}
              className={`absolute inset-x-3 max-h-[70vh] overflow-y-auto bg-white p-3 shadow-2xl ${PANEL_RADIUS_CLASS[dropdownRadius]}`}
            >
              <ul className="space-y-1">
                {menu.map((item) => (
                  <li key={item.id}>
                    {itemLink(item)}
                    {item.children.length > 0 && (
                      <ul className="ml-3 border-l border-slate-100 pl-3">
                        {item.children.map((child) => (
                          <li key={child.id}>{itemLink(child, true)}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>,
          document.body,
        )}
    </>
  );
}
